import * as React from "react";
import { ButtonType, CustomButton, Table, AlertMessage, AlertType } from "soprasteria-ui-library";
import { useRouteMatch } from "react-router";
import { Link } from "react-router-dom";
import { GENERAL_DENTAL_REFERRAL } from "../../../../utils/RouteConstants";
import { IGeneralDentalReferral } from "../../../../models/IGeneralDentalReferral";
import { useAuth } from "../../../../App";
import { GeneralDentalReferralAdministrationApi } from '../../../../api/GeneralDentalReferralAdministrationApi';
import { setMessage } from "../../../../helpers/MessageHelper";
import { dateTimeToDateString } from "../../../../helpers/DateHelper";
import { NotAllowedModal } from "../Modals/NotAllowedModal";
import { Console } from "console";

interface IGeneralDentalReferralTab {
    canCreateNewGeneralDentalReferral: boolean;
    generalDentalReferrals: IGeneralDentalReferral[];
    isInRoleAllmäntandvård: boolean;
    isInRoleOrtodontist: boolean;
    reloadData: () => void;
    isLoading: (loading: boolean) => void;
}

export const GeneralDentalReferralTab: React.FunctionComponent<IGeneralDentalReferralTab> = ({ canCreateNewGeneralDentalReferral, generalDentalReferrals, isInRoleAllmäntandvård, isInRoleOrtodontist, reloadData, isLoading }) => {

    let match = useRouteMatch();
    let modalId = "not-allowed-to-see-general-dental-referral-modal";
    const auth = useAuth();
    const api = new GeneralDentalReferralAdministrationApi(auth);

    const [anyDeletableGeneralDentalReferrals, setanyDeletableGeneralDentalReferrals] = React.useState<boolean>(false);
    const [successMessage, setSuccessMessage] = React.useState<string>("");
    const [errorMessage, setErrorMessage] = React.useState<string>("");

    React.useEffect(() => {
        setanyDeletableGeneralDentalReferrals(generalDentalReferrals.some(r => r.canBeDeleted === true));
    }, [generalDentalReferrals])

    const renderTableLink = (linkText: string, generalDentalReferral: IGeneralDentalReferral) => {
        if (generalDentalReferral.isAllowedToAccessGeneralDentalReferral) {
            return <Link to={`${match.url}/${GENERAL_DENTAL_REFERRAL}/${generalDentalReferral.generalDentalReferralId}`}>{linkText}</Link>
        } else {
            return <a onClick={() => showModal()}>{linkText}</a>
        }
    }

    const removeGeneralDentalReferral = (generalDentalReferralId: number | undefined) => {
        const accepted = window.confirm("Är du säker på att du vill radera Allmäntandvårdsremiss?");
        if (accepted) {
            isLoading(true);
            if (generalDentalReferralId) {
                api.deleteGeneralDentalReferral(generalDentalReferralId.toString(), () => {
                    changeSuccessMessage("Allmäntandvårdsremiss raderad");
                    reloadData();
                }, (error) => {
                    changeErrorMessage(error);
                }, () => {
                    isLoading(false);
                });
            }
        }
    }

    const changeSuccessMessage = (message: string) => {
        const newMessage = setMessage(message, successMessage ?? "");
        setSuccessMessage(newMessage);
    }

    const changeErrorMessage = (message: string) => {
        const newMessage = setMessage(message, errorMessage ?? "");
        setErrorMessage(newMessage);
    }

    const showModal = () => {
        document.getElementById(`open-${modalId}`)?.click();
    }

    return (
        <section>
            {
                canCreateNewGeneralDentalReferral ?
                    <div className="panel text-right">
                        <Link to={`${match.url}/${GENERAL_DENTAL_REFERRAL}`}><CustomButton buttonText="Skapa allmäntandvårdsremiss" buttonType={ButtonType.create} /></Link>
                    </div>
                    : null
            }
            <NotAllowedModal notAllowedText={isInRoleOrtodontist ? "Begäran om aktivt medgivande har skickats till remittenten. Begäran har ännu inte bifallits" : "Du har ej behörighet att se den valda remissen"} id={modalId} />
            <AlertMessage alertType={AlertType.danger} duration={3000}>{errorMessage}</AlertMessage>
            <AlertMessage alertType={AlertType.success} duration={3000}>{successMessage}</AlertMessage>
            <Table hoverableRows>
                <thead>
                    <tr>
                        <th>Datum skickad</th>
                        <th>Remiss-ID</th>
                        <th>Remittent</th>
                        <th>Remissmottagare</th>
                        <th>Status</th>
                        {
                            anyDeletableGeneralDentalReferrals ?
                                <th className="delete-btn">Ta bort</th>
                                : null
                        }
                    </tr>
                </thead>
                <tbody>
                    {
                        generalDentalReferrals.length > 0 ?
                            generalDentalReferrals.map((generalDentalReferral, key) => {
                                return (
                                    <tr key={key} className="clickable-row">
                                        <td className="table-data-link">{renderTableLink(generalDentalReferral.sentDate ? dateTimeToDateString(generalDentalReferral.sentDate) : "", generalDentalReferral)}</td>
                                        <td className="table-data-link">{renderTableLink(generalDentalReferral.generalDentalReferralNumber ?? "", generalDentalReferral)}</td>
                                        <td className="table-data-link">{renderTableLink(generalDentalReferral.referrerCareProvider.name ?? "", generalDentalReferral)}</td>
                                        <td className="table-data-link">{renderTableLink(generalDentalReferral.recipientCareProvider.name ?? "", generalDentalReferral)}</td>
                                        <td className="table-data-link">{renderTableLink(generalDentalReferral.generalDentalReferralStatus ?? "", generalDentalReferral)}</td>
                                        {
                                            anyDeletableGeneralDentalReferrals ?
                                                <td className="delete-btn table-data-link">
                                                    {
                                                        generalDentalReferral.canBeDeleted ?
                                                            <CustomButton
                                                                buttonType={ButtonType.delete}
                                                                onClick={() => removeGeneralDentalReferral(generalDentalReferral.generalDentalReferralId)}
                                                                buttonText="" />
                                                            : null
                                                    }
                                                </td>
                                                : null
                                        }
                                    </tr>
                                )
                            })
                            :
                            <tr><td colSpan={anyDeletableGeneralDentalReferrals ? 6 : 5}>Det finns inga allmäntandvårdsremisser registrerade för den här patienten</td></tr>
                    }
                </tbody>
            </Table>
        </section>
    )
}